/**
 * PWA Provider Component
 *
 * Renders PWA UI elements: offline indicator and update notification.
 * Should wrap the app in main.tsx.
 *
 * Instalação do app (#291): nenhum aviso automático em lugar nenhum. O painel tem a
 * opção manual "Instalar o app Vende Aí" (menu do usuário); a loja online convida a
 * instalar o app DA LOJA uma única vez, depois do pedido (ver @utils/pwaInstall).
 * O link do manifesto do Vende Aí só existe nas rotas do painel (este provider põe e
 * tira conforme a rota; ver @utils/appManifest): fora delas o navegador não tem o que
 * oferecer para instalar.
 *
 * "Nova versão disponível" aparece só no painel. No site, na loja online e na mesa a
 * versão nova entra sozinha na próxima vez que o site for aberto (o cliente final não
 * é interrompido com um aviso técnico, que poderia recarregar a página no meio do pedido).
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { WifiOff, RefreshCw, X } from 'lucide-react';
import { usePWA } from '@hooks/usePWA';
import { useAuth } from '@hooks/useAuth';
import { isPanelPath } from '@utils/pwaInstall';
import { PANEL_MANIFEST_HREF, removeManifestLink, setManifestLink } from '@utils/appManifest';

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const { isOnline, hasUpdate, applyUpdate, dismissUpdate } = usePWA();
  const { pathname } = useLocation();
  const inPanel = isPanelPath(pathname);
  const { isAuthenticated } = useAuth();

  // Manifesto do Vende Aí só no painel, com o lojista logado (site, loja, mesa e página
  // 404 de visitante ficam sem o aviso de instalação do navegador)
  const panelManifest = inPanel && isAuthenticated;
  useEffect(() => {
    if (panelManifest) setManifestLink('panel', PANEL_MANIFEST_HREF);
    else removeManifestLink('panel');
  }, [panelManifest]);

  return (
    <>
      {children}

      {/* Offline Indicator */}
      {!isOnline && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
          <WifiOff size={16} />
          <span>Sem conexão com a internet. Alguns recursos podem não estar disponíveis.</span>
        </div>
      )}

      {/* Update Notification */}
      {hasUpdate && inPanel && (
        <div className="fixed bottom-4 right-4 z-[9998] flex max-w-sm items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-xl dark:border-blue-800 dark:bg-blue-900/90">
          <RefreshCw size={20} className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Nova versão disponível
            </p>
            <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
              Uma atualização do VendeAi está pronta para ser aplicada.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={applyUpdate}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              >
                Atualizar agora
              </button>
              <button
                onClick={dismissUpdate}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-800"
              >
                Depois
              </button>
            </div>
          </div>
          <button
            onClick={dismissUpdate}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
}
